import { useState, useEffect } from 'react'
import {
  MdFlight,
  MdSchedule,
  MdLocationOn,
  MdAirplaneTicket,
  MdTrendingUp,
  MdTrendingDown,
  MdAccessTime,
  MdCheckCircle,
  MdWarning,
  MdError,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { useFlight } from '../../hooks/useFlight'
import {
  formatTime,
  formatDuration,
  getStatusColor,
  getSeatAvailabilityColor,
  formatPrice,
} from '../../utils/flight'
import { capitalizeStatus } from '../../utils/string'
import { Button, LoadingSpinner, EmptyState } from '../ui'

const FlightList = () => {
  const navigate = useNavigate()
  const [flights, setFlights] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const { getFlightsAsync } = useFlight()

  useEffect(() => {
    loadFlights()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadFlights = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const flightsData = await getFlightsAsync()
      setFlights(flightsData || [])
    } catch (err) {
      console.error('Error loading flights:', err)
      setError('Failed to load flights')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return <MdSchedule className="text-primary-600 dark:text-primary-400" />
      case 'in flight':
      case 'inflight':
        return <MdFlight className="text-success-600 dark:text-success-400" />
      case 'delayed':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'cancelled':
        return <MdError className="text-red-600 dark:text-red-400" />
      case 'completed':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      default:
        return <MdAccessTime className="text-neutral-500 dark:text-neutral-400" />
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <Button variant="primary" onClick={loadFlights}>
          Retry
        </Button>
      </div>
    )
  }

  if (!flights || flights.length === 0) {
    return (
      <EmptyState
        icon={<MdFlight className="text-4xl" />}
        title="No flights found"
        description="There are no flights available at the moment."
        action={
          <Button variant="primary" onClick={loadFlights}>
            Refresh
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      {flights.map((flight) => (
        <div
          key={flight._id}
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200 cursor-pointer"
          onClick={() => navigate(`/flights/${flight._id}`)}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Flight Header */}
            <div className="flex items-start justify-between lg:justify-start lg:space-x-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                  <MdFlight className="text-primary-600 dark:text-primary-400 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {flight.number}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">{flight.route}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 lg:hidden">
                {getStatusIcon(flight.status)}
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(flight.status)}`}
                >
                  {capitalizeStatus(flight.status)}
                </span>
              </div>
            </div>

            {/* Flight Details */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Origin */}
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <MdTrendingUp className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {flight.origin?.airport?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {flight.origin?.airport?.code} • {flight.origin?.airport?.city}
                  </p>
                  <div className="flex items-center justify-center space-x-1 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Gate {flight.origin?.gate || 'N/A'}</span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {formatTime(flight.origin?.scheduledTime)}
                  </p>
                </div>
              </div>

              {/* Flight Info */}
              <div className="flex flex-col items-center justify-center space-y-1">
                <div className="flex items-center space-x-2">
                  <MdFlight className="text-neutral-400 dark:text-neutral-500" />
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {formatDuration(flight.duration)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <MdAirplaneTicket className="text-neutral-400 dark:text-neutral-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {formatPrice(flight.price)}
                  </span>
                </div>
              </div>

              {/* Destination */}
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <MdTrendingDown className="text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {flight.destination?.airport?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {flight.destination?.airport?.code} • {flight.destination?.airport?.city}
                  </p>
                  <div className="flex items-center justify-center space-x-1 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Gate {flight.destination?.gate || 'N/A'}</span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {formatTime(flight.destination?.scheduledTime)}
                  </p>
                </div>
              </div>
            </div>

            {/* Status and Seats */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4 space-y-2 lg:space-y-0">
              <div className="hidden lg:flex items-center space-x-2">
                {getStatusIcon(flight.status)}
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(flight.status)}`}
                >
                  {capitalizeStatus(flight.status)}
                </span>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {flight.seatsAvailable}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">Available</p>
                </div>
                <div className="text-center">
                  <p
                    className={`text-sm font-medium ${getSeatAvailabilityColor(flight.seatsAvailable, flight.seats)}`}
                  >
                    {flight.seatsBooked}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">Booked</p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/flights/${flight._id}`)
                }}
              >
                View Details
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default FlightList
